import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { CardGroup, MetricCard } from "@similarweb/pro-logic-components";
import type { IChangeValue } from "@similarweb/ui-components";

import { Position as RankingDistributionPosition } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/rankingDistribution.types";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";
import { translate } from "app/filters/i18nFilter";
import { pctSignFilter, percentageFilter } from "app/filters/ngFilters";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";
import type { ValueOf } from "pro-features/types";
import type { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import type { IRankDistributionOpportunitiesData } from "../../types";
import { KeywordDistributionGroup } from "../../types";
import { valueFormatter } from "../../utils";

interface IRankingDistributionOpportunitiesProps {
    isLoading?: boolean;
    data?: IRankDistributionOpportunitiesData;
}

const defaultDataItem = {
    change: 0,
    keywordCount: 0,
    share: 0,
    totalKeywords: 0,
};
const defaultData = {
    country: null,
    data: {
        [KeywordDistributionGroup.Group1To3]: defaultDataItem,
        [KeywordDistributionGroup.Group4To10]: defaultDataItem,
        [KeywordDistributionGroup.Group11To20]: defaultDataItem,
        [KeywordDistributionGroup.Group21To50]: defaultDataItem,
        [KeywordDistributionGroup.Other]: defaultDataItem,
    },
} as IRankDistributionOpportunitiesData;
const FractionLength = 1;
function metricCardClicked(
    group: RankingDistributionPosition,
    cardParams: { duration: string; key: string; pageFilter: string; webSource: ValueOf<typeof devicesTypes> },
    country: number,
): () => void {
    const parsedPageFilter = safeJsonParse(cardParams.pageFilter, []) as { url: string }[];
    const proNavigatorParams = {
        ...cardParams,
        country,
        isWWW: false,
        ranking: `${parsedPageFilter[0]?.url}:${group}`,
    };
    return (): void => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", `Ranking Opportunities/${group}`);
        void proNavigator.go("organicsearch_website_search_ranking_distribution_v2", proNavigatorParams);
    };
}
function changeValueProps(value: number): IChangeValue {
    return {
        value: `${pctSignFilter()(percentageFilter()(value || 0, FractionLength))}`,
        isDecrease: value < 0,
        invertColors: false,
    };
}
export const RankingDistributionOpportunities: FC<IRankingDistributionOpportunitiesProps> = ({
    isLoading = true,
    data = defaultData,
}: IRankingDistributionOpportunitiesProps) => {
    const group1To3Data = data.data[KeywordDistributionGroup.Group1To3];
    const group4To10Data = data.data[KeywordDistributionGroup.Group4To10];
    const group11To20Data = data.data[KeywordDistributionGroup.Group11To20];
    const group21To50Data = data.data[KeywordDistributionGroup.Group21To50];
    const country = data.country;
    const { params } = useCurrentStateAndParams();
    const metricCardTitlePrefix = "website.seo.overview.rankingdistribution.opportunities";
    const ctaIconName = "arrow-slant-right";
    const cardParams = params as {
        duration: string;
        key: string;
        pageFilter: string;
        webSource: ValueOf<typeof devicesTypes>;
    };

    return (
        <CardGroup
            $height="102px"
            withDividers={true}
        >
            <MetricCard
                isLoading={isLoading}
                ctaIconName={ctaIconName}
                title={translate(`${metricCardTitlePrefix}.group1.title`)}
                tooltip={translate(`${metricCardTitlePrefix}.group1.tooltip`)}
                value={valueFormatter(group1To3Data?.keywordCount)}
                valueDescriptor={translate("common.keywords")}
                changeValueProps={changeValueProps(group1To3Data?.change)}
                onCardClick={metricCardClicked(RankingDistributionPosition.OneToThree, cardParams, country)}
            />
            <MetricCard
                isLoading={isLoading}
                ctaIconName={ctaIconName}
                title={translate(`${metricCardTitlePrefix}.group2.title`)}
                tooltip={translate(`${metricCardTitlePrefix}.group2.tooltip`)}
                value={valueFormatter(group4To10Data?.keywordCount)}
                valueDescriptor={translate("common.keywords")}
                changeValueProps={changeValueProps(group4To10Data?.change)}
                onCardClick={metricCardClicked(RankingDistributionPosition.FourToTen, cardParams, country)}
            />
            <MetricCard
                isLoading={isLoading}
                ctaIconName={ctaIconName}
                title={translate(`${metricCardTitlePrefix}.group3.title`)}
                tooltip={translate(`${metricCardTitlePrefix}.group3.tooltip`)}
                value={valueFormatter(group11To20Data?.keywordCount)}
                changeValueProps={changeValueProps(group11To20Data?.change)}
                onCardClick={metricCardClicked(RankingDistributionPosition.ElevenToTwenty, cardParams, country)}
            />
            <MetricCard
                isLoading={isLoading}
                ctaIconName={ctaIconName}
                title={translate(`${metricCardTitlePrefix}.group4.title`)}
                tooltip={translate(`${metricCardTitlePrefix}.group4.tooltip`)}
                value={valueFormatter(group21To50Data?.keywordCount)}
                valueDescriptor={translate("common.keywords")}
                changeValueProps={changeValueProps(group21To50Data?.change)}
                onCardClick={metricCardClicked(RankingDistributionPosition.TwentyOneToFifty, cardParams, country)}
            />
        </CardGroup>
    );
};
